import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, Button } from "@tellescope/ui";
import { Select, Label, Input } from "@tellescope/design-system";
import { ChevronDown } from "lucide-react";
import type { StripeSubscriptionsBlock, CancelIframeConfig, PauseIframeConfig } from "../../../../../shared/schema";
import type { VisibilityCondition } from "@tellescope/visibility-schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";

type StripeSubscriptionsBlockEditorProps = BlockTypeEditorProps<StripeSubscriptionsBlock>;

export function StripeSubscriptionsBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: StripeSubscriptionsBlockEditorProps) {
  const { t } = useTranslation();

  // Get current height as number (convert preset to pixels if needed)
  const getHeightAsNumber = (height: CancelIframeConfig["height"] | undefined): number => {
    if (typeof height === "number") return height;
    const presets: Record<string, number> = { sm: 200, md: 400, lg: 600, xl: 800 };
    return presets[height ?? "md"] ?? 400;
  };

  const [cancelIframeHeightInput, setCancelIframeHeightInput] = useState(
    String(getHeightAsNumber(block.props.cancelIframe?.height))
  );

  const handleCancelIframeHeightBlur = () => {
    const numValue = parseInt(cancelIframeHeightInput, 10);
    if (!isNaN(numValue) && numValue >= 100 && numValue <= 2000) {
      updateCancelIframe({ height: numValue });
    } else {
      setCancelIframeHeightInput(String(getHeightAsNumber(block.props.cancelIframe?.height)));
    }
  };

  const updateCancelIframe = (updates: Partial<CancelIframeConfig> | undefined) => {
    if (updates === undefined) {
      updateProps({ cancelIframe: undefined });
    } else {
      updateProps({
        cancelIframe: {
          url: "",
          height: "md",
          passToken: true,
          tokenParam: "token",
          subscriptionIdParam: "subscriptionId",
          ...block.props.cancelIframe,
          ...updates,
        },
      });
    }
  };

  const [pauseIframeHeightInput, setPauseIframeHeightInput] = useState(
    String(getHeightAsNumber(block.props.pauseIframe?.height))
  );

  const handlePauseIframeHeightBlur = () => {
    const numValue = parseInt(pauseIframeHeightInput, 10);
    if (!isNaN(numValue) && numValue >= 100 && numValue <= 2000) {
      updatePauseIframe({ height: numValue });
    } else {
      setPauseIframeHeightInput(String(getHeightAsNumber(block.props.pauseIframe?.height)));
    }
  };

  const updatePauseIframe = (updates: Partial<PauseIframeConfig> | undefined) => {
    if (updates === undefined) {
      updateProps({ pauseIframe: undefined });
    } else {
      updateProps({
        pauseIframe: {
          url: "",
          height: "md",
          passToken: true,
          tokenParam: "token",
          subscriptionIdParam: "subscriptionId",
          ...block.props.pauseIframe,
          ...updates,
        },
      });
    }
  };

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 5)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="20">20</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No subscriptions")}
        />
      </div>

      {/* Subscription Management Actions */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-sm font-medium">{t("Subscription Actions")}</Label>
        <p className="text-xs text-muted-foreground mb-3">
          {t("Enable actions that users can perform on their subscriptions. All actions are disabled by default.")}
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="allowCancel"
              checked={block.props.allowCancel ?? false}
              onCheckedChange={(checked) => updateProps({ allowCancel: !!checked })}
            />
            <label htmlFor="allowCancel" className="text-sm cursor-pointer">
              {t("Allow Cancel Subscription")}
            </label>
          </div>

          {/* Custom Cancel Iframe Configuration */}
          {block.props.allowCancel && (
            <Collapsible className="ml-6 border-l-2 border-muted pl-4">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-auto p-1 text-xs gap-1">
                  <ChevronDown className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-180" />
                  {t("Custom Cancel Flow (Iframe)")}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="useCancelIframe"
                    checked={!!block.props.cancelIframe}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updateCancelIframe({ url: "" });
                      } else {
                        updateCancelIframe(undefined);
                      }
                    }}
                  />
                  <label htmlFor="useCancelIframe" className="text-sm cursor-pointer">
                    {t("Use custom iframe for cancellation")}
                  </label>
                </div>

                {block.props.cancelIframe && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs">{t("Iframe URL")} *</Label>
                      <Input
                        value={block.props.cancelIframe.url}
                        onChange={(e) => updateCancelIframe({ url: e.target.value })}
                        placeholder="https://example.com/cancel"
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">{t("Height (pixels)")}</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={100}
                          max={2000}
                          value={cancelIframeHeightInput}
                          onChange={(e) => setCancelIframeHeightInput(e.target.value)}
                          onBlur={handleCancelIframeHeightBlur}
                          className="h-8 text-sm w-24"
                        />
                        <span className="text-xs text-muted-foreground">px</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">{t("Subscription ID Parameter")}</Label>
                      <Input
                        value={block.props.cancelIframe.subscriptionIdParam ?? "subscriptionId"}
                        onChange={(e) =>
                          updateCancelIframe({ subscriptionIdParam: e.target.value || "subscriptionId" })
                        }
                        placeholder="subscriptionId"
                        className="h-8 text-sm w-40"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("Query param name for the Stripe subscription ID")}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="cancelIframePassToken"
                          checked={block.props.cancelIframe.passToken ?? true}
                          onCheckedChange={(checked) => updateCancelIframe({ passToken: !!checked })}
                        />
                        <label htmlFor="cancelIframePassToken" className="text-xs cursor-pointer">
                          {t("Pass authentication token")}
                        </label>
                      </div>
                      {(block.props.cancelIframe.passToken ?? true) && (
                        <div className="pl-6 pt-1">
                          <Label className="text-xs">{t("Token Parameter")}</Label>
                          <Input
                            value={block.props.cancelIframe.tokenParam ?? "token"}
                            onChange={(e) => updateCancelIframe({ tokenParam: e.target.value || "token" })}
                            placeholder="token"
                            className="h-8 text-sm w-32 mt-1"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="allowUndoCancel"
              checked={block.props.allowUndoCancel ?? false}
              onCheckedChange={(checked) => updateProps({ allowUndoCancel: !!checked })}
            />
            <label htmlFor="allowUndoCancel" className="text-sm cursor-pointer">
              {t("Allow Undo Cancellation")}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="allowPause"
              checked={block.props.allowPause ?? false}
              onCheckedChange={(checked) => updateProps({ allowPause: !!checked })}
            />
            <label htmlFor="allowPause" className="text-sm cursor-pointer">
              {t("Allow Pause Subscription")}
            </label>
          </div>

          {/* Custom Pause Iframe Configuration */}
          {block.props.allowPause && (
            <Collapsible className="ml-6 border-l-2 border-muted pl-4">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-auto p-1 text-xs gap-1">
                  <ChevronDown className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-180" />
                  {t("Custom Pause Flow (Iframe)")}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="usePauseIframe"
                    checked={!!block.props.pauseIframe}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updatePauseIframe({ url: "" });
                      } else {
                        updatePauseIframe(undefined);
                      }
                    }}
                  />
                  <label htmlFor="usePauseIframe" className="text-sm cursor-pointer">
                    {t("Use custom iframe for pause")}
                  </label>
                </div>

                {block.props.pauseIframe && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs">{t("Iframe URL")} *</Label>
                      <Input
                        value={block.props.pauseIframe.url}
                        onChange={(e) => updatePauseIframe({ url: e.target.value })}
                        placeholder="https://example.com/pause"
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">{t("Height (pixels)")}</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={100}
                          max={2000}
                          value={pauseIframeHeightInput}
                          onChange={(e) => setPauseIframeHeightInput(e.target.value)}
                          onBlur={handlePauseIframeHeightBlur}
                          className="h-8 text-sm w-24"
                        />
                        <span className="text-xs text-muted-foreground">px</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">{t("Subscription ID Parameter")}</Label>
                      <Input
                        value={block.props.pauseIframe.subscriptionIdParam ?? "subscriptionId"}
                        onChange={(e) => updatePauseIframe({ subscriptionIdParam: e.target.value || "subscriptionId" })}
                        placeholder="subscriptionId"
                        className="h-8 text-sm w-40"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("Query param name for the Stripe subscription ID")}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="pauseIframePassToken"
                          checked={block.props.pauseIframe.passToken ?? true}
                          onCheckedChange={(checked) => updatePauseIframe({ passToken: !!checked })}
                        />
                        <label htmlFor="pauseIframePassToken" className="text-xs cursor-pointer">
                          {t("Pass authentication token")}
                        </label>
                      </div>
                      {(block.props.pauseIframe.passToken ?? true) && (
                        <div className="pl-6 pt-1">
                          <Label className="text-xs">{t("Token Parameter")}</Label>
                          <Input
                            value={block.props.pauseIframe.tokenParam ?? "token"}
                            onChange={(e) => updatePauseIframe({ tokenParam: e.target.value || "token" })}
                            placeholder="token"
                            className="h-8 text-sm w-32 mt-1"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="allowResume"
              checked={block.props.allowResume ?? false}
              onCheckedChange={(checked) => updateProps({ allowResume: !!checked })}
            />
            <label htmlFor="allowResume" className="text-sm cursor-pointer">
              {t("Allow Resume Subscription")}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="allowUpdatePaymentMethod"
              checked={block.props.allowUpdatePaymentMethod ?? false}
              onCheckedChange={(checked) => updateProps({ allowUpdatePaymentMethod: !!checked })}
            />
            <label htmlFor="allowUpdatePaymentMethod" className="text-sm cursor-pointer">
              {t("Allow Update Payment Method")}
            </label>
          </div>
        </div>
      </div>

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which subscriptions are displayed based on conditions. Use 'Stripe Subscriptions' source for subscription properties like status and product."
        )}
      />
      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
